import { useState, useEffect } from 'react';
import { API_CONFIG } from '../config/api';

// ดึงค่าจาก data รองรับทั้งแบบแบน (value เป็น string) และแบบจากแอดมิน (value เป็น object มี .value)
const getStr = (d, key, fallback = '') => {
  if (!d) return fallback;
  const v = d[key] ?? d[key.replace('seo_', '')];
  if (v == null) return fallback;
  if (typeof v === 'string') return v.trim() || fallback;
  if (typeof v === 'object' && v !== null && typeof v.value === 'string') return v.value.trim() || fallback;
  return fallback;
};

/**
 * Custom hook to fetch and apply SEO settings from the database
 * Updates document.title and meta tags based on SEO settings
 */
export const useSEOSettings = () => {
  const [seoSettings, setSeoSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const applyTitleAndMeta = (siteTitle, metaDescription, keywords) => {
      const defaultTitle = 'PT Thai Plus - แพลตฟอร์มหาเทรนเนอร์ออกกำลังกาย';
      const title = (siteTitle || defaultTitle).trim() || defaultTitle;
      document.title = title;
      const titleEl = document.querySelector('title');
      if (titleEl) titleEl.textContent = title;

      const desc = (metaDescription || 'PT Thai Plus - แพลตฟอร์มเทรนเนอร์ออกกำลังกายมืออาชีพ').trim();
      let metaDescTag = document.querySelector('meta[name="description"]');
      if (!metaDescTag) {
        metaDescTag = document.createElement('meta');
        metaDescTag.setAttribute('name', 'description');
        document.head.appendChild(metaDescTag);
      }
      metaDescTag.setAttribute('content', desc);

      if (keywords && keywords.trim()) {
        let metaKeywordsTag = document.querySelector('meta[name="keywords"]');
        if (!metaKeywordsTag) {
          metaKeywordsTag = document.createElement('meta');
          metaKeywordsTag.setAttribute('name', 'keywords');
          document.head.appendChild(metaKeywordsTag);
        }
        metaKeywordsTag.setAttribute('content', keywords.trim());
      }
    };

    const fetchJson = async (url) => {
      const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch SEO settings (status ${response.status})`);
      }
      return response.json();
    };

    const fetchSEOSettings = async () => {
      try {
        const apiUrl = API_CONFIG.BASE_URL;
        const t = Date.now();
        // 1) Preferred public endpoint
        let result = await fetchJson(`${apiUrl}/public/seo-settings?t=${t}`);
        // 2) Fallback: some deployments expose this via /admin router without auth
        if (result?.success && result?.data && Object.keys(result.data).length === 0) {
          try {
            result = await fetchJson(`${apiUrl}/admin/public/seo-settings?t=${t}`);
          } catch (e) {
            // ignore fallback error, will use default below
          }
        }

        if (result?.success && result?.data && Object.keys(result.data).length > 0) {
          setSeoSettings(result.data);
          const d = result.data;
          const siteTitle = getStr(d, 'seo_site_title') || getStr(d, 'site_title') || '';
          const metaDescription = getStr(d, 'seo_meta_description') || getStr(d, 'site_description') || '';
          const keywords = getStr(d, 'seo_keywords') || getStr(d, 'site_keywords') || '';
          applyTitleAndMeta(siteTitle, metaDescription, keywords);
        } else {
          applyTitleAndMeta('', '', '');
        }
      } catch (error) {
        console.error('❌ Error fetching SEO settings:', error);
        applyTitleAndMeta('', '', '');
      } finally {
        setLoading(false);
      }
    };

    fetchSEOSettings();
  }, []);

  return { seoSettings, loading };
};

export default useSEOSettings;

